import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getVMs,
  getVM,
  createVM,
  deleteVM,
  startVM,
  stopVM,
  restartVM,
  getVMConsole,
  getTemplates,
  toggleOmer,
  getTrashedVMs,
  restoreVM,
  purgeVM,
} from "@/api/endpoints/vms";
import type { VMCreatePayload } from "@/types";
import { useActiveTenant } from "@/components/tenant/TenantSelector";

export function useVMs() {
  const [tenantId] = useActiveTenant();

  return useQuery({
    queryKey: ["vms", tenantId],
    queryFn: () => getVMs(tenantId ?? undefined),
    enabled: !!tenantId,
  });
}

export function useVM(vmId: number) {
  return useQuery({
    queryKey: ["vm", vmId],
    queryFn: () => getVM(vmId),
    enabled: vmId > 0,
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: ["templates"],
    queryFn: getTemplates,
  });
}

export function useCreateVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VMCreatePayload) => createVM(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["billing-events"] });
      queryClient.invalidateQueries({ queryKey: ["billing-summary"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-quota"] });
    },
  });
}

export function useDeleteVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vmId: number) => deleteVM(vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vlans"] });
      queryClient.invalidateQueries({ queryKey: ["billing-events"] });
      queryClient.invalidateQueries({ queryKey: ["billing-summary"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-quota"] });
    },
  });
}

export function useStartVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vmId: number) => startVM(vmId),
    onSuccess: (_data, vmId) => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vm", vmId] });
    },
  });
}

export function useStopVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vmId: number) => stopVM(vmId),
    onSuccess: (_data, vmId) => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vm", vmId] });
    },
  });
}

export function useRestartVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vmId: number) => restartVM(vmId),
    onSuccess: (_data, vmId) => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vm", vmId] });
    },
  });
}

export function useToggleOmer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vmId: number) => toggleOmer(vmId),
    onSuccess: (_data, vmId) => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vm", vmId] });
    },
  });
}

export function useTrashedVMs() {
  return useQuery({
    queryKey: ["vms", "trash"],
    queryFn: getTrashedVMs,
  });
}

export function useRestoreVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vmId: number) => restoreVM(vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
    },
  });
}

export function usePurgeVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vmId: number) => purgeVM(vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["billing-events"] });
    },
  });
}

export function useVMConsole(vmId: number) {
  return useQuery({
    queryKey: ["vm-console", vmId],
    queryFn: () => getVMConsole(vmId),
    enabled: false, // Only fetch when manually triggered
    gcTime: 0, // Don't cache — VNC tickets expire quickly
    staleTime: 0,
  });
}
